/**
 * Campo de data UNIFORME de todo o HUB (portado da DEMO-03, telas/CampoData.tsx): digitação em DD/MM/AAAA com
 * máscara, validação de data real e calendário acessível por teclado
 * (setas, Page Up/Down, Home/End, Enter e Esc). O valor trafega em ISO.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { isoParaBr, lerDataBr, mascararData } from '../../../backend/src/compartilhado/documentos.ts';

/** Hoje no fuso da INTTRIX (America/Sao_Paulo), em ISO. */
export const hojeIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const DIAS_SEMANA_NOME = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const iso = (a: number, m: number, d: number) => `${a}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const somarDias = (s: string, n: number) => new Date(Date.parse(`${s}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const somarMeses = (s: string, n: number) => {
  const [a, m, d] = s.split('-').map(Number);
  const t = m - 1 + n;
  const ano = a + Math.floor(t / 12);
  const mes = ((t % 12) + 12) % 12;
  const ultimo = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
  return iso(ano, mes, Math.min(d, ultimo));
};

type Retangulo = { top: number; bottom: number; left: number; right: number; height: number };
/**
 * E0: posição do calendário aberto, numa camada fixa, INTEIRO dentro da área visível (a janela modal, quando houver,
 * e a tela): embaixo do campo; sem espaço, em cima; sem espaço nos dois, ao lado (nunca por cima do campo). Corrige o
 * calendário cortado e a rolagem lateral nas janelas em 1440×700 e 1024×700.
 */
export function posicaoCalendario(campo: Retangulo, largura: number, altura: number, limite: Retangulo | null, tela: { largura: number; altura: number }) {
  const m = 8;
  const area = { top: Math.max(0, limite?.top ?? 0), bottom: Math.min(tela.altura, limite?.bottom ?? tela.altura), left: Math.max(0, limite?.left ?? 0), right: Math.min(tela.largura, limite?.right ?? tela.largura) };
  const entre = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));
  const horizontal = entre(campo.left, area.left + m, area.right - m - largura);
  if (campo.bottom + 4 + altura <= area.bottom - m) return { top: campo.bottom + 4, left: horizontal };
  if (campo.top - 4 - altura >= area.top + m) return { top: campo.top - 4 - altura, left: horizontal };
  const direita = campo.right + m + largura <= area.right - m;
  const left = direita ? campo.right + m : Math.max(area.left + m, campo.left - m - largura);
  // área mais baixa que o calendário: alinha ao topo da tela (nunca fora dela)
  const top = entre(campo.top + campo.height / 2 - altura / 2, Math.max(m, area.top + m), Math.max(m, Math.min(area.bottom, tela.altura) - m - altura));
  return { top, left };
}

export function CampoData({
  rotulo,
  valor,
  aoMudar,
  teste,
  min,
  max,
  somenteLeitura,
  desabilitado,
  obrigatorio,
  ajuda,
  classe,
}: {
  rotulo: string;
  valor: string;
  aoMudar?: (iso: string) => void;
  teste?: string;
  min?: string;
  max?: string;
  somenteLeitura?: boolean;
  desabilitado?: boolean;
  obrigatorio?: boolean;
  ajuda?: string;
  classe?: string;
}) {
  const id = useId();
  const [texto, setTexto] = useState(isoParaBr(valor));
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(false);
  const [foco, setFoco] = useState(valor || hojeIso());
  const botao = useRef<HTMLButtonElement>(null);
  const grade = useRef<HTMLDivElement>(null);
  const linha = useRef<HTMLDivElement>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const [posicao, setPosicao] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!aberto) return setPosicao(null);
    const posicionar = () => {
      if (!caixa.current || !linha.current) return;
      const modal = linha.current.closest('.modal');
      setPosicao(posicaoCalendario(linha.current.getBoundingClientRect(), caixa.current.offsetWidth, caixa.current.offsetHeight, modal ? modal.getBoundingClientRect() : null, { largura: window.innerWidth, altura: window.innerHeight }));
    };
    posicionar();
    window.addEventListener('resize', posicionar);
    window.addEventListener('scroll', posicionar, true);
    return () => {
      window.removeEventListener('resize', posicionar);
      window.removeEventListener('scroll', posicionar, true);
    };
  }, [aberto, foco]);
  useEffect(() => {
    // Valor alterado de fora (ex.: recálculo): reflete no texto, sem reescrever o que já corresponde a ele.
    const r = lerDataBr(texto);
    if (!(r.ok && r.iso === valor)) setTexto(isoParaBr(valor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);
  // o foco vai ao dia quando o calendário já está posicionado (E0: antes disso ele ainda está sendo medido)
  const posicionado = posicao !== null;
  useEffect(() => {
    if (aberto && posicionado) grade.current?.querySelector<HTMLButtonElement>(`[data-dia="${foco}"]`)?.focus();
  }, [aberto, foco, posicionado]);

  const confirmar = (t: string, final: boolean) => {
    if (!t) {
      setErro(obrigatorio && final ? 'Informe a data (DD/MM/AAAA).' : '');
      if (!obrigatorio) aoMudar?.('');
      return;
    }
    if (t.length < 10) {
      setErro(final ? 'Data incompleta: use DD/MM/AAAA.' : '');
      return;
    }
    const r = lerDataBr(t);
    if (!r.ok) return setErro(r.erro);
    if (min && r.iso < min) return setErro(`A data não pode ser anterior a ${isoParaBr(min)}.`);
    if (max && r.iso > max) return setErro(`A data não pode ser posterior a ${isoParaBr(max)}.`);
    setErro('');
    aoMudar?.(r.iso);
  };
  const escolher = (dia: string) => {
    setTexto(isoParaBr(dia));
    confirmar(isoParaBr(dia), true);
    setAberto(false);
    botao.current?.focus();
  };
  const [ano, mes] = foco.split('-').map(Number);
  const primeiro = new Date(Date.UTC(ano, mes - 1, 1)).getUTCDay();
  const dias = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  const celulas = [...Array(primeiro).fill(''), ...Array.from({ length: dias }, (_, i) => iso(ano, mes - 1, i + 1))];
  const tecla = (e: KeyboardEvent) => {
    const mapa: Record<string, () => string> = {
      ArrowLeft: () => somarDias(foco, -1),
      ArrowRight: () => somarDias(foco, 1),
      ArrowUp: () => somarDias(foco, -7),
      ArrowDown: () => somarDias(foco, 7),
      PageUp: () => somarMeses(foco, -1),
      PageDown: () => somarMeses(foco, 1),
      Home: () => somarDias(foco, -new Date(`${foco}T00:00:00Z`).getUTCDay()),
      End: () => somarDias(foco, 6 - new Date(`${foco}T00:00:00Z`).getUTCDay()),
    };
    if (mapa[e.key]) {
      e.preventDefault();
      setFoco(mapa[e.key]());
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setAberto(false);
      botao.current?.focus();
    }
  };
  const foraDoLimite = (dia: string) => Boolean((min && dia < min) || (max && dia > max));
  return (
    <div className={`campo-data${classe ? ` ${classe}` : ''}`} data-teste={teste ? `${teste}-campo` : undefined}>
      <label htmlFor={id}>
        {rotulo}
        {obrigatorio && <span aria-hidden> *</span>}
      </label>
      <div className="campo-data-linha" ref={linha}>
        <input
          id={id}
          value={texto}
          inputMode="numeric"
          placeholder="DD/MM/AAAA"
          maxLength={10}
          readOnly={somenteLeitura}
          disabled={desabilitado}
          aria-invalid={Boolean(erro)}
          aria-describedby={erro || ajuda ? `${id}-msg` : undefined}
          aria-required={obrigatorio}
          onChange={(e) => {
            const t = mascararData(e.target.value);
            setTexto(t);
            confirmar(t, false);
          }}
          onBlur={() => confirmar(texto, true)}
          data-teste={teste}
        />
        <button
          type="button"
          ref={botao}
          className="botao-calendario"
          aria-label={`${somenteLeitura ? 'Ver' : 'Abrir'} calendário: ${rotulo}`}
          aria-expanded={aberto}
          aria-haspopup="dialog"
          disabled={desabilitado}
          onClick={() => {
            setFoco(valor || (min && hojeIso() < min ? min : hojeIso()));
            setAberto(!aberto);
          }}
          data-teste={teste ? `${teste}-calendario` : undefined}
        >
          <span aria-hidden>📅</span>
        </button>
      </div>
      {(erro || ajuda) && (
        <small id={`${id}-msg`} className={erro ? 'erro-campo' : 'ajuda'} role={erro ? 'alert' : undefined}>
          {erro || ajuda}
        </small>
      )}
      {aberto && (
        <div
          className="calendario calendario-flutuante"
          ref={caixa}
          role="dialog"
          aria-label={`Calendário: ${rotulo}`}
          onKeyDown={tecla}
          style={posicao ? { top: posicao.top, left: posicao.left } : { opacity: 0, top: 0, left: 0 }}
          data-teste={teste ? `${teste}-calendario-aberto` : undefined}
        >
          <div className="calendario-topo">
            <button type="button" className="botao-secundario pequeno" onClick={() => setFoco(somarMeses(foco, -1))} aria-label="Mês anterior">
              ‹
            </button>
            <strong aria-live="polite">
              {MESES[mes - 1]} de {ano}
            </strong>
            <button type="button" className="botao-secundario pequeno" onClick={() => setFoco(somarMeses(foco, 1))} aria-label="Próximo mês">
              ›
            </button>
          </div>
          <div className="calendario-grade" ref={grade} role="group" aria-label="Dias do mês">
            {DIAS_SEMANA.map((x, i) => (
              <span key={`s${i}`} className="calendario-semana" aria-hidden>
                {x}
              </span>
            ))}
            {celulas.map((dia, i) =>
              dia ? (
                <button
                  key={dia}
                  type="button"
                  data-dia={dia}
                  tabIndex={dia === foco ? 0 : -1}
                  className={`calendario-dia${dia === valor ? ' selecionado' : ''}${dia === hojeIso() ? ' hoje' : ''}`}
                  aria-label={`${Number(dia.slice(8))} de ${MESES[mes - 1]} de ${ano}, ${DIAS_SEMANA_NOME[new Date(`${dia}T00:00:00Z`).getUTCDay()]}${dia === valor ? ' (selecionada)' : ''}`}
                  aria-pressed={dia === valor}
                  disabled={somenteLeitura || foraDoLimite(dia)}
                  onClick={() => escolher(dia)}
                >
                  {Number(dia.slice(8))}
                </button>
              ) : (
                <span key={`v${i}`} />
              ),
            )}
          </div>
          <div className="calendario-rodape">
            {!somenteLeitura && (
              <button type="button" className="botao-link" onClick={() => escolher(hojeIso())} disabled={foraDoLimite(hojeIso())}>
                Hoje ({isoParaBr(hojeIso())})
              </button>
            )}
            <button
              type="button"
              className="botao-link"
              onClick={() => {
                setAberto(false);
                botao.current?.focus();
              }}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Data e hora (atividades, agendamentos): mesmo campo de data + hora HH:MM. */
export function CampoDataHora({ rotulo, valor, aoMudar, teste, obrigatorio }: { rotulo: string; valor: string; aoMudar: (isoDataHora: string) => void; teste?: string; obrigatorio?: boolean }) {
  const [data, hora] = valor ? valor.split('T') : ['', ''];
  return (
    <div className="campo-data-hora">
      <CampoData rotulo={`${rotulo} — data`} valor={data} aoMudar={(d) => aoMudar(d ? `${d}T${hora || '09:00'}` : '')} teste={teste} obrigatorio={obrigatorio} />
      <label className="campo-hora">
        {rotulo} — hora
        <input type="time" value={hora ?? ''} onChange={(e) => aoMudar(`${data || hojeIso()}T${e.target.value}`)} data-teste={teste ? `${teste}-hora` : undefined} />
      </label>
    </div>
  );
}
